import { DateTime } from 'luxon';
import { SidebarTrigger } from '../ui/sidebar';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
  useAction,
  useInScopeModel,
  useWatchableUpdatedAt,
  useWatchableValue,
} from '@cotera/client/v0/actions/framework';
import { GraphModel } from '../../graph/graph.model';
import { PublishGraphAction } from '../../graph/publish.action';
import { cn, displayShortcut } from '../../lib/utils';
import React, { useEffect, useRef, useState } from 'react';
import { useTabViewsState } from '../../tab-manager';
import { Button } from '../ui/button';
import { Icon, Text, Tooltip } from '@cotera/client/app/components/ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  DisableAutoPublishAction,
  EnableAutoPublishAction,
} from '../../graph/toggle-auto-publish.action';
import { useCommits } from '../inner-layout';
import { CreditBalanceSection } from './credit-balance-section';

export const Topbar: React.FC = () => {
  const [tabs] = useTabViewsState();
  const dndContextRef = useRef<HTMLDivElement>(null);

  return (
    <header
      ref={dndContextRef}
      className="flex h-10 shrink-0 border-b border-border w-full items-center"
    >
      <DndProvider backend={HTML5Backend} context={dndContextRef}>
        <div className="px-2">
          <SidebarTrigger />
        </div>
        <div className="flex items-center h-full flex-1 overflow-x-auto thin-scrollbar">
          {Object.entries(tabs).map(([_, tabView], index) => (
            <React.Fragment key={index}>{tabView}</React.Fragment>
          ))}
        </div>
        <div className="flex items-center justify-center relative gap-2">
          <CreditBalanceSection />
          <Notifications />
          <PublishSection />
        </div>
      </DndProvider>
    </header>
  );
};

const PublishSection: React.FC = () => {
  const graph = useInScopeModel(GraphModel);
  const autoPublish = useWatchableValue(graph.autoPublish);
  const enableAutoPublish = useAction(EnableAutoPublishAction);
  const disableAutoPublish = useAction(DisableAutoPublishAction);
  const publishAction = useAction(PublishGraphAction);
  const publishable = useWatchableValue(graph.publishable);
  const publishableUpdatedAt = useWatchableUpdatedAt(graph.publishable);
  const hasUnpublishedOperations = useWatchableValue(
    graph.hasUnpublishedOperations
  );

  return (
    <div className="flex items-center h-full">
      <Tooltip
        side="bottom"
        tooltipContent={
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              {publishable &&
                (publishAction.action?.shortcut !== undefined
                  ? displayShortcut(publishAction.action.shortcut)
                  : 'Publish changes')}
              {!publishable && (
                <span>
                  This graph is not currently publishable because there are
                  invalid changes.
                </span>
              )}
            </div>
            <Text.Caption>
              Last updated:{' '}
              {DateTime.fromMillis(publishableUpdatedAt).toLocaleString(
                DateTime.DATETIME_SHORT
              )}
            </Text.Caption>
          </div>
        }
      >
        <Button
          variant={'ghost'}
          className="rounded-none h-full border-l border-border text-foreground/80 hover:text-foreground rounded-r-none"
          disabled={
            (!autoPublish && !hasUnpublishedOperations) ||
            autoPublish ||
            !publishable
          }
          onClick={async () => {
            await publishAction.execute({});
          }}
        >
          {!publishable && (
            <Icon icon="warning" size="small" className="text-destructive" />
          )}
          {autoPublish ? 'Auto Publish' : 'Publish'}
        </Button>
      </Tooltip>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="rounded-none h-full border-l border-border text-foreground/80 hover:text-foreground rounded-l-none px-2"
          >
            <Icon icon="chevron-down" size="small" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {autoPublish ? (
            <DropdownMenuItem onClick={() => disableAutoPublish.execute({})}>
              Disable Auto Publish
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onClick={() => enableAutoPublish.execute({})}>
              Enable Auto Publish
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

const Notifications: React.FC = () => {
  const graphModel = useInScopeModel(GraphModel);
  // If server-side has commits that aren't yet reflected locally.
  const [remoteHasNewCommits, setRemoteHasNewCommits] = useState(false);

  const {
    data: serverSideCommits,
    refetch: refetchCommits,
    isFetching,
  } = useCommits();

  const clientSideCommits = graphModel.publishedCommits.snapshot();

  useEffect(() => {
    if (serverSideCommits.length > clientSideCommits.length) {
      setRemoteHasNewCommits(true);
    }
  }, [serverSideCommits, clientSideCommits]);

  const handleRefreshCommits = async () => {
    await refetchCommits();
    graphModel.refreshPublishedCommitsCache(serverSideCommits);
    setRemoteHasNewCommits(false);
  };

  const notifications = [
    remoteHasNewCommits ? (
      <Button
        variant="ghost"
        className="flex gap-2 items-center"
        onClick={handleRefreshCommits}
        disabled={isFetching}
      >
        <span>New changes are available. Click to refresh.</span>
      </Button>
    ) : undefined,
  ].filter((x) => x !== undefined);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          disabled={notifications.length === 0}
          className={cn(
            'relative',
            notifications.length === 0
              ? 'text-muted-foreground'
              : 'text-foreground'
          )}
        >
          <Icon icon="bell" />
          {notifications.length > 0 && (
            <div className="absolute top-[10%] left-[55%] bg-yellow-300 text-white text-xs rounded-full w-3 h-3 flex items-center justify-center px-1" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {notifications.length === 0 && (
          <DropdownMenuItem>
            <span>No notifications</span>
          </DropdownMenuItem>
        )}
        {notifications.map((notification, index) => (
          <DropdownMenuItem key={index}>{notification}</DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
